import { type Ref, useId, useMemo } from "react"

import { useMediaQuery } from "../../../../hooks/useMediaQuery"
import blinkSvg from "./marvin-idle-blink.svg?raw"

export interface MarvinAnimationProps {
  size?: number
  className?: string
  loop?: boolean
  framing?: "canvas" | "icon"
  ref?: Ref<SVGSVGElement>
}

const stripRoot = (source: string) =>
  source
    .trim()
    .replace(/^<svg[^>]*>/, "")
    .replace(/<\/svg>$/, "")
const staticArtwork = stripRoot(blinkSvg).replace(/<animate\b[\s\S]*?<\/animate>/g, "")

/** Internal renderer for trusted bundled artwork. Import a named variant in consumers. */
export function MarvinAnimatedSvg({
  source,
  size = 28,
  className,
  loop = false,
  framing = "canvas",
  ref,
}: MarvinAnimationProps & { source: string }) {
  const id = useId()
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)")
  const markup = useMemo(
    () => ({
      __html: (reducedMotion ? staticArtwork : stripRoot(source))
        .replaceAll("__MARVIN_ID__", `marvin-${id.replaceAll(":", "")}`)
        .replaceAll("__MARVIN_REPEAT__", loop ? "indefinite" : "1"),
    }),
    [source, id, loop, reducedMotion],
  )

  return (
    <svg
      ref={ref}
      width={size}
      height={size}
      // Match the resting footprint used by MarvinIdleIcon, without resizing the SVG element.
      viewBox={framing === "icon" ? "80 79.2549 320 320" : "0 0 480 480"}
      // The jump may leave the resting footprint; keep its motion visible.
      overflow={framing === "icon" ? "visible" : undefined}
      fill="currentColor"
      className={className}
      aria-hidden="true"
      dangerouslySetInnerHTML={markup}
    />
  )
}
